<script setup>

import { inject, ref } from 'vue';
import { apiModifierInfosChamp } from '@/services/ServiceModele.js';
import ModaleAjoutParametre from '@/components/modales/ModaleAjoutParametre.vue';
import ModaleAjoutConstante from '@/components/modales/ModaleAjoutConstante.vue';
import ModaleAjoutDimension from '@/components/modales/ModaleAjoutDimension.vue';
import ModaleEditionTypeObjet from '@/components/modales/ModaleEditionTypeObjet.vue';
import ModaleEditionChamp from '@/components/modales/ModaleEditionChamp.vue';

const { formule, affichageSuggestions } = defineProps({
	formule: Object,
	affichageSuggestions: Boolean
});

const emit = defineEmits(['modifTexteFormule', 'doubleClic']);

const getTypeObjet = inject('getTypeObjet');
const getDimension = inject('getDimension');
const getChamp = inject('getChamp');
const afficherBarreFlottante = inject('afficherBarreFlottante');
const appelerApiModifModele = inject('appelerApiModifModele');

const elements = ref([]);
const affichageModaleAjoutParametre = ref(null);
const parametreSuggere = ref(null);
const constanteSuggeree = ref(null);
const dimensionSuggeree = ref(null);
const typeObjetSuggere = ref(null);
const champSuggere = ref(null);

function getClasse(typeElement) {
	switch(typeElement) {
		case 'VALEUR_LISTE':
			return 'expr-valeur-liste';
		case 'TYPE_OBJET':
			return 'expr-type-objet';
		case 'CHAMP':
			return 'expr-champ';
		case 'PARAMETRE':
			return 'expr-parametre';
		case 'DIMENSION':
			return 'expr-dimension';
		case 'ELEMENT_GLOBAL':
			return 'expr-elt-global';
		case 'OPERATEUR':
			return 'expr-operateur';
		case 'ERREUR':
			return 'expr-erreur';
		case 'CONSTANTE':
		case 'LITTERAL':
			return 'expr-litteral';
		case 'PARENTHESE':
			return 'expr-parenthese';
		case 'SEPARATEUR':
			return 'expr-separateur';
		case 'MOT_CLE':
			return 'expr-mot-cle';
		case 'FONCTION':
			return 'expr-fonction';
		case 'VARIABLE':
			return 'expr-variable';
		case 'ESPACES':
			return 'expr-espaces';
		case 'RETOUR_LIGNE':
			return 'expr-retour-ligne';
		case 'COMMENTAIRE':
			return 'expr-commentaire';
		case 'IGNORE':
			return 'expr-ignore';
		default:
			return '';
	}
}

function afficherSuggestions(suggestions, indiceElement) {
	const itemsSuggestion = suggestions.map(suggestion => creerItemSuggestion(suggestion, indiceElement));
	afficherBarreFlottante({
		items: itemsSuggestion, 
		selectionItems: [],
		composantParent: elements.value[indiceElement],
		affichageVertical: true,
		titre: 'Suggestions'
	});
}

function creerItemSuggestion(suggestion, indiceElement) {
	if (suggestion.type === 'SuggestionAjoutChamp') {
		return {
			libelle: `Créer le champ ${suggestion.champ.nom} pour le type d'objet ${getTypeObjet(suggestion.champ.idTypeObjet).nom}`,
			icones: ['fa fa-table-columns'],
			commande: () => afficherModaleAjoutChamp(suggestion.champ)
		};
	}
	if (suggestion.type === 'SuggestionAjoutParametre') {
		return {
			libelle: `Créer le parametre ${suggestion.parametre.nom}`,
			icones: ['fa fa-sliders'],
			commande: () => afficherModaleAjoutParametre(suggestion.parametre)
		};
	}
	if (suggestion.type === 'SuggestionAjoutConstante') {
		return {
			libelle: `Créer la constante ${suggestion.constante.nom}`,
			icones: ['fa fa-c'],
			commande: () => afficherModaleAjoutConstante(suggestion.constante)
		};
	}
	if (suggestion.type === 'SuggestionAjoutDimension') {
		return {
			libelle: `Créer la dimension ${suggestion.dimension.nom}`,
			icones: ['fa fa-cube'],
			commande: () => afficherModaleAjoutDimension(suggestion.dimension)
		};
	}
	if (suggestion.type === 'SuggestionAjoutTypeObjet') {
		return {
			libelle: `Créer le type d'objet ${suggestion.typeObjet.nom}`,
			icones: ['fa fa-sitemap'],
			commande: () => afficherModaleAjoutTypeObjet(suggestion.typeObjet)
		};
	}
	if (suggestion.type === 'SuggestionAjoutDimensionAChamp') {
		const champ = getChamp(suggestion.idTypeObjet, suggestion.idChamp);
		const dimension = getDimension(suggestion.idDimension);
		return {
			libelle: `Ajouter la dimension ${dimension.nom} au champ ${champ.nom}`,
			icones: ['fa fa-cube'],
			commande: () => ajouterDimensionAChamp(champ, dimension)
		};
	}
	if (suggestion.type === 'SuggestionModifTexte') {
		return {
			libelle: `Modifier en "${suggestion.texteModifie}"`,
			icones: ['fa fa-keyboard'],
			commande: () => modifierTexteEltFormule(indiceElement, suggestion.texteModifie)
		};
	}
	if (suggestion.type === 'SuggestionSupprTexte') {
		return {
			libelle: `Supprimer`,
			icones: ['fa fa-trash'],
			commande: () => modifierTexteEltFormule(indiceElement, '')
		};
	}
}

function afficherModaleAjoutChamp(champ) {
	champSuggere.value = champ;
}

function afficherModaleAjoutParametre(parametre) {
	parametreSuggere.value = parametre;
	affichageModaleAjoutParametre.value = true;
}

function afficherModaleAjoutConstante(constante) {
	constanteSuggeree.value = constante;
}

function afficherModaleAjoutDimension(dimension) {
	dimensionSuggeree.value = dimension;
}

function afficherModaleAjoutTypeObjet(typeObjet) {
	typeObjetSuggere.value = typeObjet;
}

function ajouterDimensionAChamp(champ, dimension) {
	const champModifie = { ...champ };
	const dimensions = champ.dimensions.map(idDim => getDimension(idDim));
	dimensions.push(dimension);
	champModifie.dimensions = dimensions;
	appelerApiModifModele(apiModifierInfosChamp, champModifie);
}

function modifierTexteEltFormule(indiceElement, texteModifie) {
	const textes = formule.elements.map(elt => elt.texte);
	textes[indiceElement] = texteModifie;
	emit('modifTexteFormule', textes.join(''));
}

</script>

<template>
    <div class="panel-formule texte-expression" 
		@dblclick="$emit('doubleClic')">
		<template v-for="(element, indiceElement) in formule?.elements">
			<span v-if="element.type !== 'RETOUR_LIGNE'"
				:ref="el => elements[indiceElement] = el"
				class="element-formule"
				:class="getClasse(element.type)"
				v-tooltip="element.desc">
				{{ element.texte }}<button 
					v-if="affichageSuggestions && element.suggestions?.length"
					class="bouton btn-actions fa-solid fa-caret-down" 
					@click.stop="afficherSuggestions(element.suggestions, indiceElement)" />
			</span>
			<br v-else>
		</template>
		<ModaleEditionChamp 
			v-if="champSuggere"
			:champ-edite="champSuggere"
			@fermeture="champSuggere = null" />
		<ModaleAjoutParametre 
			v-if="affichageModaleAjoutParametre"
			v-model:visible="affichageModaleAjoutParametre"
			:parametre="parametreSuggere" />
		<ModaleAjoutConstante 
			v-if="constanteSuggeree"
			:constante="constanteSuggeree"
			@fermeture="constanteSuggeree = null" />
		<ModaleAjoutDimension 
			v-if="dimensionSuggeree"
			:dimension="dimensionSuggeree"
			@fermeture="dimensionSuggeree = null" />
		<ModaleEditionTypeObjet 
			v-if="typeObjetSuggere"
			:type-objet-edite="typeObjetSuggere"
			@fermeture="typeObjetSuggere = null" />		
	</div>
</template>

<style scoped>

.panel-formule {
	padding: 2px 5px;
	white-space: pre;
	height: 100%;
	background-color: inherit;
}

.element-formule {
	position: relative;
	display: inline-block;
}

.btn-actions {
	zoom: 70%;
	bottom: -10px;
	right: -10px;
	left: unset;
	top: unset;
}

</style>